import { dispatch } from '../store/canvas-store';
import type { Frame } from '../store/types';
import { resolveCfNode } from '../store/slot-schema';

/**
 * Make the selected layer's live element editable. On commit (blur / Enter):
 * - if the element is a `data-slot` inside an instance → instance.setSlot
 *   (preserves the Living-Components contract);
 * - otherwise → frame.editNode on the owning free/main frame (a non-slot node
 *   inside an instance edits the underlying Main).
 * Escape cancels. Mirrors chrome/inlineSlotEdit.ts.
 */
export function beginLayerTextEdit(frame: Frame, cfNodeId: string): void {
  const host = document.querySelector(`[data-frame-id="${CSS.escape(frame.id)}"]`);
  const el = host ? (resolveCfNode(host, cfNodeId) as HTMLElement | null) : null;
  if (!el) return;

  const slotName = el.getAttribute('data-slot');
  const isInstanceSlot = frame.kind === 'instance' && !!slotName;
  const original = el.innerText;

  el.contentEditable = 'plaintext-only';
  el.focus();
  const sel = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(el);
  sel?.removeAllRanges();
  sel?.addRange(range);

  let done = false;
  const cleanup = () => {
    el.contentEditable = 'false';
    el.removeEventListener('blur', onBlur);
    el.removeEventListener('keydown', onKeyDown);
  };
  const commit = () => {
    if (done) return;
    done = true;
    const value = el.innerText;
    cleanup();
    if (value === original) return;
    if (isInstanceSlot) {
      dispatch({ t: 'instance.setSlot', id: frame.id, slotName: slotName!, value });
    } else {
      const targetId = frame.kind === 'instance' ? frame.mainId : frame.id;
      dispatch({ t: 'frame.editNode', frameId: targetId, cfNodeId, text: value });
    }
  };
  const onBlur = () => commit();
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      done = true;
      el.innerText = original;
      cleanup();
      e.stopPropagation();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
      el.blur();
    }
  };
  el.addEventListener('blur', onBlur);
  el.addEventListener('keydown', onKeyDown);
}
